import React, { useState } from 'react'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import {
	COUNTRY_DETAILS,
} from '../../../../constants/countryDetails'
import { CountryFlagIcon } from '../../../../constants/countryFlagIcons'
import SelectButtonSpinner from '../../../SelectButtonSpinner/SelectButtonSpinner'
import ResearchKodaBlock from '../../../ResearchKodaBlock/ResearchKodaBlock'
import { useResearchKodaBlock } from '../../../../hooks/useResearchKodaBlock'
import s from './styles/CountryCard.module.scss'

const POLICY_CLAMP = 80

function PolicyRow({ icon, label, text }) {
	const [expanded, setExpanded] = useState(false)
	if (!text) return null
	const isLong = text.length > POLICY_CLAMP

	return (
		<div className={s.policy}>
			<span className={s.policy__icon}>{icon}</span>
			<div className={s.policy__body}>
				<span className={s.policy__label}>{label}</span>
				<p className={s.policy__text}>
					{!isLong || expanded ? text : `${text.slice(0, POLICY_CLAMP)}...`}
					{isLong ? (
						<button
							type="button"
							className={s.readMore}
							onClick={event => {
								event.stopPropagation()
								setExpanded(value => !value)
							}}
						>
							{expanded ? 'Read less' : 'Read more'}
						</button>
					) : null}
				</p>
			</div>
		</div>
	)
}

export default function CountryCard({
	country,
	selected,
	onSelect,
	busy = false,
	busySelf = false,
}) {
	const details = COUNTRY_DETAILS[country.title] || {}
	const visa = country.raw?.visa_policies || {}
	const languages = details.language || []
	const jobPortals = details.jobPortals || []
	const tags = [
		country.isTopJobs ? 'Most Jobs' : null,
		visa.pr_policy === 'Easy' ? 'Immigration Friendly' : null,
	].filter(Boolean)

	const { title: kodaTitle, actions: kodaActions, onActionClick } =
		useResearchKodaBlock('country', { country: country.title })

	return (
		<article
			className={`${s.card} ${selected ? s.selected : ''} ${busy ? s.busy : ''}`}
		>
			<header className={s.head}>
				{tags.length ? (
					<div className={s.tags}>
						{tags.map(tag => (
							<span key={tag} className={s.tag}>
								⭐ {tag}
							</span>
						))}
					</div>
				) : null}
				<div className={s.head__row}>
					<span className={s.icon}>
						<CountryFlagIcon country={country.title} className={s.icon__svg} />
					</span>
					<h3 className={s.title}>{country.title}</h3>
					<button
						type="button"
						disabled={busy}
						onClick={() => onSelect(country.title)}
						className={`${s.btn} ${selected ? s.active : ''} ${busySelf ? s.btnBusy : ''}`}
					>
						{busy ? (
							<SelectButtonSpinner />
						) : selected ? (
							<>
								<CheckIcon className={s.btn__icon} />
								Selected
							</>
						) : (
							'Select'
						)}
					</button>
				</div>
			</header>

			{country.region ? (
				<div className={s.region}>
					📍 Recommended region <strong>{country.region}</strong>
				</div>
			) : null}

			<div className={s.body}>
				<div className={s.metrics}>
					<div className={s.metric}>
						<span className={s.metric__label}>Average salary</span>
						<strong className={s.metric__value}>💵 {country.salary || '—'}</strong>
					</div>
					<div className={s.metric}>
						<span className={s.metric__label}>Number of jobs</span>
						<strong className={s.metric__value}>💼 {country.jobs || '—'}</strong>
					</div>
				</div>

				{languages.length ? (
					<div className={s.languages}>
						{languages.map(item => (
							<div key={item.language} className={s.language}>
								<span className={s.language__icon}>📊</span>
								<div className={s.language__body}>
									<span className={s.language__title}>
										{item.language}: <span>{item.level}</span>
									</span>
									<span className={s.language__caption}>Language test</span>
								</div>
							</div>
						))}
					</div>
				) : null}

				<div className={s.policies}>
					<div className={s.policies__title}>
						<span className={s.policies__line} />
						Immigration policies
						<span className={s.policies__line} />
					</div>
					<PolicyRow
						icon="🛫"
						label="Post study work visa"
						text={visa.post_study_visa}
					/>
					<PolicyRow
						icon="🏠"
						label="Permanent Residency"
						text={
							visa.pr_policy || visa.ideal_pr_program
								? [visa.pr_policy, visa.ideal_pr_program]
										.filter(Boolean)
										.join(': ')
								: ''
						}
					/>
				</div>

				<ResearchKodaBlock
					title={kodaTitle}
					actions={kodaActions}
					onActionClick={onActionClick}
				/>

				{jobPortals.length ? (
					<div className={s.sources}>
						<span className={s.sources__label}>Sources:</span>
						<div className={s.sources__list}>
							{jobPortals.map(portal => (
								<span key={portal} className={s.sources__item}>
									{portal}
								</span>
							))}
						</div>
					</div>
				) : null}
			</div>
		</article>
	)
}
